import { CONFIG, type GameConfig } from "../config.js";
import type { ActivityName } from "../protocol.js";
import type { RoleDef } from "../roles.js";

export type Activity = ActivityName;

export interface StepInput {
  activity: Activity;
  exhaustion: number;
  burnedOut: boolean;
  role: RoleDef;
  /** Already multiplied by `TIME_SCALE`. */
  dt: number;
}

export type StepEvent = "burnout" | "rested" | "coffee_done";

export interface StepOutput {
  exhaustion: number;
  burnedOut: boolean;
  /** Work-units credited this step. */
  contributed: number;
  activity: Activity;
  event?: StepEvent;
}

/**
 * Advance one player by `dt` seconds. Pure: same input always yields the same
 * output. Handles the partial-tick case where a working player would cross
 * `MAX_EXHAUSTION_S` mid-step by crediting only the remaining fraction.
 */
export function stepPlayer(i: StepInput, c: GameConfig = CONFIG): StepOutput {
  let exhaustion = clamp(i.exhaustion, 0, c.MAX_EXHAUSTION_S);
  let burnedOut = i.burnedOut;
  let activity = i.activity;
  let contributed = 0;
  let event: StepEvent | undefined;

  if (i.dt > 0) {
    if (activity === "working") {
      const rate = c.WORK_EXHAUSTION_RATE * i.role.exhaustionRate;
      const room = c.MAX_EXHAUSTION_S - exhaustion;
      const gain = i.dt * rate;

      if (rate > 0 && gain >= room) {
        const effectiveDt = room / rate;
        contributed = effectiveDt * i.role.workRate;
        exhaustion = c.MAX_EXHAUSTION_S;
        burnedOut = true;
        activity = "idle";
        event = "burnout";
      } else {
        exhaustion = clamp(exhaustion + gain, 0, c.MAX_EXHAUSTION_S);
        contributed = i.dt * i.role.workRate;
      }
    } else if (activity === "coffee") {
      exhaustion = Math.max(0, exhaustion - i.dt * c.COFFEE_RECOVERY_RATE);
      if (exhaustion === 0) {
        activity = "idle";
        event = "coffee_done";
      }
    } else {
      exhaustion = Math.max(0, exhaustion - i.dt * c.IDLE_RECOVERY_RATE);
    }
  }

  if (burnedOut && exhaustion <= c.BURNOUT_CLEAR_THRESHOLD) {
    burnedOut = false;
    event ??= "rested";
  }

  return { exhaustion, burnedOut, contributed, activity, event };
}

/**
 * Recovery applied to an offline record on rejoin. Identical to idling in the
 * office, so relogging gives neither advantage nor penalty.
 */
export function recoverOffline(
  exhaustion: number,
  burnedOut: boolean,
  offlineSeconds: number,
  c: GameConfig = CONFIG,
): { exhaustion: number; burnedOut: boolean } {
  const e = Math.max(0, exhaustion - Math.max(0, offlineSeconds) * c.IDLE_RECOVERY_RATE);
  const clears = burnedOut && e <= c.BURNOUT_CLEAR_THRESHOLD;
  return { exhaustion: e, burnedOut: clears ? false : burnedOut };
}

export function exhaustionPct(exhaustion: number, c: GameConfig = CONFIG): number {
  return clamp(exhaustion / c.MAX_EXHAUSTION_S, 0, 1) * 100;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}
